// Pure formatting helpers for the Analytics dashboard. No Vue, no I/O, so they
// can be unit-tested in isolation.

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const compactFormatter = new Intl.NumberFormat('en-US', {
	notation: 'compact',
	maximumFractionDigits: 1,
});

/**
 * Plain integer with thousands separators. Null/undefined/NaN -> "0".
 */
export function formatNumber(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return '0';
	return numberFormatter.format(Math.round(n));
}

/**
 * Compact display for large counts: 950 -> "950", 12_400 -> "12.4K",
 * 1_200_000 -> "1.2M". Values under 10_000 stay exact.
 */
export function formatCompact(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return '0';
	if (Math.abs(n) < 10000) return numberFormatter.format(Math.round(n));
	return compactFormatter.format(n);
}

function pad2(n) {
	return n < 10 ? `0${n}` : String(n);
}

/**
 * Total watch time from milliseconds. Short, human form used in KPI cards and
 * tables:
 *   45_000        -> "45s"
 *   4 * 60_000    -> "4m"
 *   11_520_000    -> "3h 12m"
 *   >= 1000 hours -> "1.2K h" (short unit so KPI values stay on one line)
 */
export function formatWatchTime(ms) {
	const totalSeconds = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
	if (totalSeconds === 0) return '0m';
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	if (hours >= 1000) return `${compactFormatter.format(hours)} h`;
	if (hours >= 1) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
	if (minutes >= 1) return `${minutes}m`;
	return `${seconds}s`;
}

/**
 * Average view time from milliseconds, always minutes + zero-padded seconds
 * below an hour: 248_000 -> "4m 08s", 5_000 -> "0m 05s", 3_720_000 -> "1h 02m".
 */
export function formatAvgViewTime(ms) {
	const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000) || 0);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	if (hours >= 1) return `${hours}h ${pad2(minutes)}m`;
	return `${minutes}m ${pad2(seconds)}s`;
}

/**
 * Exact duration for exports: h:mm:ss (hours are not capped at 24).
 * 11_525_000 -> "3:12:05".
 */
export function formatDurationHms(ms) {
	const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000) || 0);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	return `${hours}:${pad2(minutes)}:${pad2(seconds)}`;
}

/**
 * Percentage share of a part in a whole: 0..100 with one decimal below 10%.
 * Returns "0%" when the whole is 0.
 */
export function formatShare(part, whole) {
	const p = Number(part) || 0;
	const w = Number(whole) || 0;
	if (w <= 0) return '0%';
	const pct = (p / w) * 100;
	if (pct >= 99.95) return '100%';
	if (pct < 0.05) return '<0.1%';
	return pct < 10 ? `${pct.toFixed(1)}%` : `${Math.round(pct)}%`;
}

/**
 * Change vs the previous period.
 * Returns { kind: 'new' | 'flat' | 'up' | 'down' | 'none', pct, label }.
 *   - 'none' when the current value is missing (nothing to compare)
 *   - 'new'  when previous is 0 and current > 0
 *   - 'flat' when both are 0 or the rounded change is 0%
 */
export function formatDelta(current, previous) {
	if (current === null || current === undefined) return { kind: 'none', pct: null, label: '' };
	const cur = Number(current);
	if (!Number.isFinite(cur)) return { kind: 'none', pct: null, label: '' };
	const prev = previous === null || previous === undefined ? NaN : Number(previous);
	if (!Number.isFinite(prev) || prev === 0) {
		if (cur === 0) return { kind: 'flat', pct: 0, label: '0%' };
		return { kind: 'new', pct: null, label: 'New' };
	}
	const pct = ((cur - prev) / prev) * 100;
	const rounded = Math.abs(pct) >= 100 ? Math.round(pct) : Number(pct.toFixed(Math.abs(pct) < 10 ? 1 : 0));
	if (rounded === 0) return { kind: 'flat', pct: 0, label: '0%' };
	const label = `${Math.abs(rounded)}%`;
	return { kind: rounded > 0 ? 'up' : 'down', pct: rounded, label };
}

/**
 * Format a metric value for display by metric key.
 */
export function formatMetricValue(metricKey, value, { compact = true } = {}) {
	switch (metricKey) {
		case 'totalHoursWatched':
			return formatWatchTime(value);
		case 'avgViewTime':
			return formatAvgViewTime(value);
		default:
			return compact ? formatCompact(value) : formatNumber(value);
	}
}

/**
 * Relative "Updated ... ago" label for the header.
 */
export function formatUpdatedAgo(timestamp, now = Date.now()) {
	if (!timestamp) return '';
	const diff = Math.max(0, now - timestamp);
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return 'Updated just now';
	if (minutes === 1) return 'Updated 1 min ago';
	if (minutes < 60) return `Updated ${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours === 1) return 'Updated 1 hour ago';
	if (hours < 24) return `Updated ${hours} hours ago`;
	return 'Updated over a day ago';
}
